import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { DataEditor, GridCellKind, CompactSelection } from '@glideapps/glide-data-grid';
import '@glideapps/glide-data-grid/dist/index.css';

// =============================================================================
// MantleDataGrid
// Thin wrapper around Glide Data Grid (canvas-rendered) supplying the shared
// Mantle chrome: light/dark theme that follows the app's `.dark` class, sizing
// via ResizeObserver, the overlay portal Glide needs, range COPY/PASTE enabled
// by default, built-in search (Ctrl+F or the toolbar button), and the standard
// hint/toolbar strip.
//
// Why Glide and not ReactGrid: Glide paints cells to a <canvas>, so an
// interaction repaints pixels instead of re-rendering React components — no
// per-cell React reconciliation at all. See pages/People/Structures.jsx for the
// reference integration; OPEX and other grids can adopt the same pattern.
//
// Pages provide (all ReactGrid-free, Glide-native):
//   columns          [{ id, title, width }]
//   rowCount         number of data rows (include your totals row if you add one)
//   getCellContent   ([col, row]) => GridCell  (build with `gcell` helpers below)
//   onCellEdited     ([col, row], newValue) => void
//   onCellClicked    optional ([col, row], event) => void  (event.bounds = cell rect)
//   freezeColumns    sticky-left column count
//   toolbar / hint   strip content, same idea as MantleGrid
// =============================================================================

// ---- Cell builders — keep pages terse and consistent ------------------------
export const gcell = {
  text(v, { readonly = false, theme } = {}) {
    const s = v ?? '';
    return {
      kind: GridCellKind.Text, data: s, displayData: s,
      allowOverlay: !readonly,
      ...(theme ? { themeOverride: theme } : {}),
    };
  },
  number(v, { readonly = false, display, theme } = {}) {
    const n = Number(v) || 0;
    return {
      kind: GridCellKind.Number, data: n,
      displayData: display ?? String(n),
      allowOverlay: !readonly, contentAlign: 'right',
      ...(theme ? { themeOverride: theme } : {}),
    };
  },
  boolean(v, { readonly = false } = {}) {
    return { kind: GridCellKind.Boolean, data: !!v, allowOverlay: false, readonly };
  },
  // Pill list (e.g. "Role @ Site" chips). Always read-only.
  bubble(values, { theme } = {}) {
    return {
      kind: GridCellKind.Bubble, data: values ?? [], allowOverlay: false,
      ...(theme ? { themeOverride: theme } : {}),
    };
  },
};

// ---- Themes (mirrors gridStyles/MantleGrid look) -----------------------------
const FONT = "'Inter', 'Segoe UI', system-ui, sans-serif";

const LIGHT_THEME = {
  accentColor: '#2563eb',
  accentLight: 'rgba(37, 99, 235, 0.12)',
  textDark: '#1e293b',
  textMedium: '#64748b',
  textLight: '#94a3b8',
  textBubble: '#1e293b',
  bgIconHeader: '#0f172a',
  fgIconHeader: '#f8fafc',
  textHeader: '#f1f5f9',
  textHeaderSelected: '#ffffff',
  bgCell: '#ffffff',
  bgCellMedium: '#f8fafc',
  bgHeader: '#0f172a',
  bgHeaderHasFocus: '#1e293b',
  bgHeaderHovered: '#1e293b',
  bgBubble: '#f1f5f9',
  bgBubbleSelected: '#e2e8f0',
  bgSearchResult: '#fef9c3',
  borderColor: '#e2e8f0',
  linkColor: '#2563eb',
  headerFontStyle: '600 11px',
  baseFontStyle: '12px',
  editorFontSize: '12px',
  fontFamily: FONT,
};

const DARK_THEME = {
  ...LIGHT_THEME,
  accentColor: '#3b82f6',
  accentLight: 'rgba(59, 130, 246, 0.18)',
  textDark: '#e2e8f0',
  textMedium: '#94a3b8',
  textLight: '#64748b',
  textBubble: '#e2e8f0',
  bgIconHeader: '#020617',
  textHeader: '#e2e8f0',
  bgCell: '#0f172a',
  bgCellMedium: '#1e293b',
  bgHeader: '#020617',
  bgHeaderHasFocus: '#1e293b',
  bgHeaderHovered: '#1e293b',
  bgBubble: '#1e293b',
  bgBubbleSelected: '#334155',
  bgSearchResult: '#713f12',
  borderColor: '#334155',
};

// Glide's horizontal scrollbar is drawn inside the grid's own height, so a
// fitHeight grid whose columns overflow has to leave room for it.
const H_SCROLLBAR = 14;

// Glide renders its overlay cell editors into #portal — make sure it exists.
const ensurePortal = () => {
  if (typeof document === 'undefined') return;
  if (!document.getElementById('portal')) {
    const el = document.createElement('div');
    el.id = 'portal';
    Object.assign(el.style, { position: 'fixed', left: '0', top: '0', zIndex: '9999' });
    document.body.appendChild(el);
  }
};

// Follows the app's dark-mode class on <html> (toggled by useTheme).
function useIsDark() {
  const [dark, setDark] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const obs = new MutationObserver(() =>
      setDark(document.documentElement.classList.contains('dark')));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

// Glide wants explicit pixel dimensions — measure the flex container.
function useSize(ref) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0].contentRect;
      setSize({ width: Math.floor(r.width), height: Math.floor(r.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

const EMPTY_SELECTION = { columns: CompactSelection.empty(), rows: CompactSelection.empty() };

const readWidths = (gridId) => {
  if (!gridId) return {};
  try { return JSON.parse(localStorage.getItem(`mdg-widths-${gridId}`) || '{}'); } catch { return {}; }
};

const MantleDataGrid = ({
  columns,
  rowCount,
  getCellContent,
  onCellEdited,
  onCellClicked,
  onCellActivated,
  onHeaderMenuClick,
  onHeaderClicked,
  onSelectionChanged, // optional observer of the Glide GridSelection
  freezeColumns = 0,
  toolbar = null,
  hint = null,
  rowHeight = 30,
  headerHeight = 34,
  gridId = null, // set to persist user column widths in localStorage
  // fitHeight: size the grid to its content (header + rows, plus the horizontal
  // scrollbar when the columns overflow) instead of filling the parent — no
  // blank canvas below the last row. The parent box should be
  // `max-h-full flex flex-col overflow-hidden` so long datasets still cap and
  // scroll while short ones shrink-wrap.
  fitHeight = false,
  // Extra breathing room below the last row in fitHeight mode. The horizontal
  // scrollbar is already accounted for; this is purely visual slack for a grid
  // that would otherwise sit flush against the bottom of its border.
  fitHeightPadding = 0,
  gridProps = {},
}) => {
  useEffect(ensurePortal, []);
  const isDark = useIsDark();
  const wrapRef = useRef(null);
  const { width, height } = useSize(wrapRef);
  const [showSearch, setShowSearch] = useState(false);

  // ---- User-resizable column widths (persisted when gridId is set) ----------
  // A manual resize takes the column out of `grow` mode (it stops auto-stretching
  // once the user has chosen a width, like Excel).
  const [widthOverrides, setWidthOverrides] = useState(() => readWidths(gridId));
  const effColumns = useMemo(
    () => columns.map((c) => (widthOverrides[c.id] ? { ...c, width: widthOverrides[c.id], grow: 0 } : c)),
    [columns, widthOverrides]
  );
  const handleColumnResize = useCallback((column, newSize) => {
    setWidthOverrides((prev) => {
      const next = { ...prev, [column.id]: Math.max(36, Math.round(newSize)) };
      if (gridId) { try { localStorage.setItem(`mdg-widths-${gridId}`, JSON.stringify(next)); } catch { /* full/blocked */ } }
      return next;
    });
  }, [gridId]);

  // ---- Selection is controlled so paste can tile across the selected range ---
  const [selection, setSelection] = useState(EMPTY_SELECTION);
  const handleSelectionChange = useCallback((s) => {
    setSelection(s);
    onSelectionChanged?.(s);
  }, [onSelectionChanged]);

  // Excel-style paste: if the selection is bigger than the copied block, tile
  // the block to fill it (so copying one cell and pasting over a range fills
  // the whole range). Values are coerced to each target cell's kind; read-only
  // cells are skipped. Returns false = we applied the edits ourselves.
  const handlePaste = useCallback((target, values) => {
    if (!values?.length || !onCellEdited) return false;
    const bh = values.length;
    const bw = Math.max(1, ...values.map((r) => r?.length || 0));
    let [x0, y0] = target;
    let w = bw, h = bh;

    const r = selection?.current?.range;
    const targetInSel = r && target[0] >= r.x && target[0] < r.x + r.width
      && target[1] >= r.y && target[1] < r.y + r.height;
    if (targetInSel && (r.width > bw || r.height > bh)) {
      x0 = r.x; y0 = r.y;
      w = Math.max(r.width, bw); h = Math.max(r.height, bh);
    }

    for (let dy = 0; dy < h; dy++) {
      for (let dx = 0; dx < w; dx++) {
        const raw = values[dy % bh]?.[dx % bw];
        if (raw === undefined) continue;
        const c = x0 + dx, row = y0 + dy;
        if (c >= effColumns.length || row >= rowCount) continue;
        const cur = getCellContent([c, row]);
        if (!cur) continue;
        if (cur.kind === GridCellKind.Boolean) {
          if (cur.readonly) continue;
          onCellEdited([c, row], { ...cur, data: /^(true|1|yes|y|x)$/i.test(String(raw).trim()) });
        } else if (cur.kind === GridCellKind.Number) {
          if (cur.allowOverlay === false) continue;
          const n = Number(String(raw).replace(/[^0-9.-]/g, ''));
          if (!Number.isFinite(n)) continue;
          onCellEdited([c, row], { ...cur, data: n });
        } else if (cur.kind === GridCellKind.Text) {
          if (cur.allowOverlay === false) continue;
          onCellEdited([c, row], { ...cur, data: String(raw) });
        }
      }
    }
    return false;
  }, [selection, effColumns, rowCount, getCellContent, onCellEdited]);

  // ---- fitHeight sizing --------------------------------------------------------
  // Header + rows + 2px of outer hairlines is the content, but it is not the
  // whole box. When the columns are wider than the viewport Glide draws a
  // horizontal scrollbar INSIDE this height, and an exact fit means it lands on
  // top of the last row — which reads as a clipped or "not adjusting" table.
  // Wide grids (a dozen period columns plus the fixed ones) hit this every time.
  // Allow for the scrollbar only when the columns actually overflow, so grids
  // that fit stay shrink-wrapped with no dead band under the last row.
  const totalColWidth = useMemo(
    () => effColumns.reduce((a, c) => a + (c.width || 0), 0),
    [effColumns]
  );
  const needsHScroll = width > 0 && totalColWidth > width;
  const contentHeight = typeof rowHeight === 'number'
    ? headerHeight + rowCount * rowHeight + 2
      + (needsHScroll ? H_SCROLLBAR : 0)
      + fitHeightPadding
    : null;
  const fit = fitHeight && contentHeight !== null;

  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', minHeight: 0,
        height: fit ? 'auto' : '100%', maxHeight: '100%',
      }}
    >
      {(toolbar || hint) && (
        <div className="shrink-0 flex items-center gap-3 px-3.5 py-2 border-b border-slate-200 dark:border-slate-700 text-[11px] text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-900">
          {hint && <span>{hint}</span>}
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setShowSearch((s) => !s)}
              title="Search (Ctrl+F)"
              className="px-3 py-1 rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              Search
            </button>
            {toolbar}
          </div>
        </div>
      )}

      <div
        ref={wrapRef}
        style={fit
          ? { height: contentHeight, flex: '0 1 auto', minHeight: 0 }
          : { flex: 1, minHeight: 0 }}
      >
        {width > 0 && height > 0 && (
          <DataEditor
            columns={effColumns}
            rows={rowCount}
            getCellContent={getCellContent}
            onCellEdited={onCellEdited}
            onCellClicked={onCellClicked}
            onCellActivated={onCellActivated}
            onHeaderMenuClick={onHeaderMenuClick}
            onHeaderClicked={onHeaderClicked}
            onColumnResize={handleColumnResize}
            width={width}
            height={height}
            theme={isDark ? DARK_THEME : LIGHT_THEME}
            rowHeight={rowHeight}
            headerHeight={headerHeight}
            freezeColumns={freezeColumns}
            rowMarkers="none"
            smoothScrollX
            smoothScrollY
            getCellsForSelection={true}
            onPaste={handlePaste}
            gridSelection={selection}
            onGridSelectionChange={handleSelectionChange}
            fillHandle={true}
            keybindings={{ search: true, downFill: true, rightFill: true }}
            showSearch={showSearch}
            onSearchClose={() => setShowSearch(false)}
            {...gridProps}
          />
        )}
      </div>
    </div>
  );
};

export default React.memo(MantleDataGrid);
